import { getEwaData, setEwaData } from "../api";
import { db } from "../../db";
import Dexie from "dexie";
import { EWA_STORAGE_LIMIT_BYTES } from "../../helpers/constants";
import { buildDocPathEnd, pathWithoutGallery } from "../../helpers";

export const VamoosInterface = {
  _currentItin: null,
  _getCurrentUser: null,

  // Initialize the interface with current itinerary and user
  _init(currentItin, getCurrentUser, setGlobalModalContent, t, navigate, urlKey, screenNameOverrideGetter, setScreenNameOverride, setEwaOpenDocumentInfo, setEwaOpenDocumentActive) {
    this._currentItin = currentItin;
    this._getCurrentUser = getCurrentUser;
    this._setGlobalModalContent = setGlobalModalContent;
    this._t = t;
    this._navigate = navigate;
    this._urlKey = urlKey;
    this._getScreenNameOverride = screenNameOverrideGetter;
    this._setScreenNameOverride = setScreenNameOverride;
    this._setEwaOpenDocumentInfo = setEwaOpenDocumentInfo;
    this._setEwaOpenDocumentActive = setEwaOpenDocumentActive;
  },

  async _handleResponse(res, error, label, data) {
    if (!res.ok || error) {
      const errorMessage = error ?? ((Array.isArray(data?.error) && data?.error[0]?.message) || data?.error);

      this._setGlobalModalContent(() => ({
        closable: true,
        message: errorMessage ?? this._t("error_api_unknown"),
        buttons: [{ text: this._t('ok'), type: 'close' }]
      }));

      throw new Error(`${label} ${res.status} - ${errorMessage}`);
    }
  },

  async _getAppData(dataScopeId, ref) {
    const { res, data, error } = await getEwaData(ref, dataScopeId);
    await this._handleResponse(res, error, 'GET', data);
    return data ?? undefined;
  },

  async _setAppData(dataScopeId, newData, ref) {
    const payload = { data: { ...newData } };

    const { res, data, error } = await setEwaData(ref, dataScopeId, payload);
    await this._handleResponse(res, error, 'SET', data);
  },

  async _setToStorage(vamoosId, ref, dataScopeId, key, value) {
    if (!key) {
      console.error("Missing key");
      return false;
    }

    try {
      const now = new Date().toISOString();

      const entries = await db.ewaStore
        .where('[vamoosId+dataScopeId+key]')
        .between(
          [vamoosId, dataScopeId, Dexie.minKey],
          [vamoosId, dataScopeId, Dexie.maxKey]
        )
        .toArray();

      const getSizeInBytes = (obj) => new TextEncoder().encode(JSON.stringify(obj)).length;

      const currentSize = entries.reduce((sum, item) => sum + getSizeInBytes(item), 0);

      const existing = entries.find(item => item.key === key);
      const existingSize = existing ? getSizeInBytes(existing) : 0;

      const newRecord = {
        ref,
        vamoosId,
        dataScopeId,
        key,
        value,
        createdAt: now,
        id: existing?.id ?? (entries.length > 0 ? entries[entries.length - 1].id + 1 : 1)
      };
      const newRecordSize = getSizeInBytes(newRecord);

      if ((currentSize - existingSize + newRecordSize) > EWA_STORAGE_LIMIT_BYTES) {
        console.warn(`1MB storage limit exceeded for vamoosId=${vamoosId} and dataScopeId=${dataScopeId}`);
        return false;
      }

      if (existing) {
        await db.ewaStore.update(existing.id, { value, updatedAt: now });
      } else {
        await db.ewaStore.add({ ref, vamoosId, dataScopeId, key, value, updatedAt: now });
      }
      return true;
    } catch (e) {
      console.error(`Failed to set storage for [vamoosId+dataScopeId+key]=${vamoosId + dataScopeId + key}`, e);
      return false;
    }
  },

  async _removeFromStorage(vamoosId, dataScopeId, key) {
    if (!key) {
      console.error('Missing key');
      return false;
    }

    try {
      const item = await db.ewaStore
        .where('[vamoosId+dataScopeId+key]')
        .equals([vamoosId, dataScopeId, key])
        .first();

      if (!item) return false;

      await db.ewaStore.delete(item.id);

      const remainingEntriesInScope = await db.ewaStore
        .where('[vamoosId+dataScopeId+key]')
        .between(
          [vamoosId, dataScopeId, Dexie.minKey],
          [vamoosId, dataScopeId, Dexie.maxKey]
        )
        .toArray();

      if (remainingEntriesInScope.length === 0) return true;

      const now = new Date().toISOString();
      await db.ewaStore.update(remainingEntriesInScope[0].id, { updatedAt: now });
      return true;
    } catch (e) {
      console.error(`Failed to remove from storage for [vamoosId+dataScopeId+key]=${vamoosId + dataScopeId + key}`, e);
      return false;
    }
  },

  async _getFromStorage(vamoosId, dataScopeId, key) {
    if (!key) {
      console.error('Missing key');
      return undefined;
    }

    try {
      const item = await db.ewaStore
        .where('[vamoosId+dataScopeId+key]')
        .equals([vamoosId, dataScopeId, key])
        .first();

      return item?.value;
    } catch (e) {
      console.error(`Failed to get from storage for [vamoosId+dataScopeId+key]=${vamoosId + dataScopeId + key}`, e);
      return undefined;
    }
  },

  getCurrentUser() {
    return this._getCurrentUser?.();
  },

  getItinerary() {
    return this._currentItin;
  },

  getData(ewaId) {
    const ewaNode = this._currentItin?.documents?.find(doc => doc.id === ewaId)?.data;
    return ewaNode ?? {};
  },

  openDocument(id) {
    if (!this._navigate || !id) {
      console.error("Unable to open document.");
      return;
    }
    const doc = this._currentItin?.documents?.find(doc => doc.id === id);
    if (doc.type === "remote") {
      window.open(doc.remoteUrl, "_blank");
    } else {
      this._setEwaOpenDocumentActive(true);

      const folderDocs = this._currentItin?.documents?.filter(doc => doc.type === "folder");
      const menuSegueIds = this._currentItin?.menu?.map(item => item.segueId);

      const getPathIdsChain = (childId, pathIds) => {
        const parentDoc = folderDocs.find(listDoc => listDoc.entries.includes(childId));
        if (!parentDoc) return pathIds;

        pathIds.unshift(parentDoc.id);

        if (menuSegueIds.includes(parentDoc.id)) {
          pathIds[0] = parentDoc.pathName;
          return pathIds;
        }

        return getPathIdsChain(parentDoc.id, pathIds);
      };

      const pathIdsChain = getPathIdsChain(doc.id, [doc.id]);
      const pathWithoutLast = pathIdsChain.slice(0, -1);
      const pathEnd = buildDocPathEnd(doc, this._currentItin?.documents, menuSegueIds);
      const pathMiddle = pathWithoutLast.length ? `${pathWithoutLast.join('/')}/` : '';
      const path = `/${this._urlKey}/documents/${pathMiddle}${pathEnd}`

      const entryPath = pathWithoutGallery(path);
      const ewaBackPath = {
        entryPath,
        returnTo: window.location.pathname
      };

      localStorage.setItem("ewaBackPath", JSON.stringify(ewaBackPath));
      const currentScreenNameOverride = this._getScreenNameOverride?.();
      const ewaOpenDocumentInfo = {
        screenName: currentScreenNameOverride,
        ewaSourcePath: window.location.pathname,
        updated: false,
      };

      this._navigate(path);
      this._setEwaOpenDocumentInfo(ewaOpenDocumentInfo);
    }
  },

  setScreenTitle(title) {
    if (!this._setScreenNameOverride || !title) {
      console.error("Unable to set screen title.");
      return;
    }
    this._setScreenNameOverride(title);
  },

  async set(dataScopeId, ...keyValuePair) {
    try {
      const [key, value] = keyValuePair;
      const user = this._getCurrentUser?.();
      if (user?.primaryEmail || key.startsWith('shared:')) {
        await this._setAppData(dataScopeId, { [key]: value }, `${this._currentItin.operatorCode}-${this._currentItin.passcode}`);
        return true;
      }

      return this._setToStorage(this._currentItin.id, `${this._currentItin.operatorCode}-${this._currentItin.passcode}`, dataScopeId, key, value)
    } catch (e) {
      console.error(`Setting app data error: ${e.message}`);
      return false;
    }
  },

  async get(dataScopeId, ...data) {
    try {
      const key = data[0];
      const user = this._getCurrentUser?.();
      if (!user?.primaryEmail && !key.startsWith('shared:')) {
        return this._getFromStorage(this._currentItin.id, dataScopeId, key);
      }

      const appData = await this._getAppData(dataScopeId, `${this._currentItin.operatorCode}-${this._currentItin.passcode}`);
      return appData?.[key]?.value ?? undefined
    } catch (e) {
      console.error(`Getting app data error: ${e.message}`);
      return undefined;
    }
  },

  async remove(dataScopeId, ...key) {
    const keyToRemove = key[0];

    try {
      const user = this._getCurrentUser?.();
      if (user?.primaryEmail || keyToRemove.startsWith('shared:')) {
        await this._setAppData(dataScopeId, { [keyToRemove]: '_undefined' }, `${this._currentItin.operatorCode}-${this._currentItin.passcode}`);
        return true;
      }
      return this._removeFromStorage(this._currentItin.id, dataScopeId, keyToRemove);

    } catch (e) {
      console.error(`Setting app data error: ${e.message}`);
      return false;
    }
  }
};

export const getVamoosInitScript = (trusted, shouldNotifyParent, docId, postMessageType) => {
  return `
  const isTrusted = ${JSON.stringify(trusted)};
  const ewaId = ${JSON.stringify(docId)};
  let hasNotifiedReady = false;

  // Global error handler
  window.onerror = function(message, source, error) {
    console.error("Error in iframe:", message, source, error);
  };

  // to notify the parent that the iframe is ready
  window.onload = function () {
    if (${JSON.stringify(shouldNotifyParent)} && !hasNotifiedReady) {
      hasNotifiedReady = true;
      window.parent.postMessage({ type: "iframe-ready" }, "*");
    }
  };

  // Setup the bridge mechanism
  const bridge = (() => {
    let callIdCounter = 0;
    const pendingCalls = {};
    
    // Clean up pending promises if iframe unloads
    window.addEventListener("beforeunload", () => {
      for (const callId in pendingCalls) {
        pendingCalls[callId].reject(new Error("Iframe unloading"));
        delete pendingCalls[callId];
      }
    });

    // Listen for messages (postMessage) from the parent.
    window.addEventListener("message", (event) => {
      const data = event.data;
      
      // Handle bridge responses.
      if (data.type === "vamoos-bridge-response" && pendingCalls[data.callId]) {
        const { resolve, reject } = pendingCalls[data.callId];
        if (data.error) {
          reject(new Error(data.error));
        } else {
          resolve(data.result);
        }
        delete pendingCalls[data.callId];
      }

      // Handle code injection requests.
      if (data.type === "injectCode") {
        try {
          const script = document.createElement("script");
          script.textContent = data.untrustedCode;
          document.body.appendChild(script);
        } catch (e) {
          console.error("Error injecting untrusted code:", e);
        }
      }
    });

    // This function sends a bridge request to the parent.
    function call(method, ...args) {          
      if (method === "openDocument") {
        parent.postMessage({
          type: ${JSON.stringify(postMessageType)},
          method,
          args,
          callId: ewaId + "|" + "bridge_" + (++callIdCounter)
        }, "*");
        return Promise.resolve();
      }    
    
      return new Promise((resolve, reject) => {
        const callId = ewaId + "|" + "bridge_" + (++callIdCounter);
        pendingCalls[callId] = { resolve, reject };
        parent.postMessage({ type: ${JSON.stringify(postMessageType)}, method, args, callId }, "*");
      });
    }
    return { call };
  })();

  // Guarded methods ensure only trusted iframes invoke certain actions
  function guardedMethod(methodName) {
    return (...args) => {
      if (!isTrusted) {
        console.warn("[Vamoos] " + methodName + " blocked, trusted is false");
        return Promise.reject("Access denied: trusted is false");
      }
      return bridge.call(methodName, ...args);
    };
  }

  // Define the Vamoos API
  window.Vamoos = {
    _listeners: {},
    addEventListener(event, callback) {
      this._listeners[event] = this._listeners[event] || [];
      this._listeners[event].push(callback);
      console.warn("[Vamoos] Added listener for event:", event);
    },
    removeEventListener(event, callback) {
      if (!this._listeners[event]) return;
      this._listeners[event] = this._listeners[event].filter(cb => cb !== callback);
      console.warn("[Vamoos] Removed listener for event:", event);
    },
    clearEventListeners() {
      this._listeners = {};
      console.warn("[Vamoos] Cleared all listeners.");
    },
    dispatchEvent(event, data) {
      if (!this._listeners[event]) return;
      this._listeners[event].forEach(cb => cb(data));
    },
    getCurrentUser: guardedMethod("getCurrentUser"),
    getItinerary: guardedMethod("getItinerary"),
    openDocument: guardedMethod("openDocument"),
    set: (...args) => bridge.call("set", ...args),
    get: (...args) => bridge.call("get", ...args),
    remove: (...args) => bridge.call("remove", ...args),
    getData: (...args) => bridge.call("getData", ewaId, ...args),
    setScreenTitle: (...args) => bridge.call("setScreenTitle", ...args),
  };

  // Listen for custom postMessage events dispatched by the parent
  window.addEventListener("message", (event) => {
    if (event.data && event.data.type === "vamoosEvent") {
      const { event: evt, data } = event.data;
      if (evt === "clearEventListeners" && window.Vamoos.clearEventListeners) {
        window.Vamoos.clearEventListeners();
      } else {
        window.Vamoos.dispatchEvent(evt, data);
      }
    }
  });
  `;
};